import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    HOLE_COUNT,
    holeBet,
    holePayout,
    computeHole,
    computeStandings,
    formatMoney,
    allocateStrokes,
} from "@/lib/game";
import Scorecard from "@/components/Scorecard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function Chevron({ dir = "left" }) {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
        >
            {dir === "left" ? (
                <polyline points="15 18 9 12 15 6" />
            ) : (
                <polyline points="9 18 15 12 9 6" />
            )}
        </svg>
    );
}

export default function PlayScreen({
    players,
    course,
    holes,
    setHoles,
    onFinish,
    onExit,
}) {
    const [current, setCurrent] = useState(() => {
        // resume at first hole without a committed result
        const idx = holes.findIndex((h) => !h.result);
        return idx === -1 ? HOLE_COUNT - 1 : idx;
    });
    const [showScorecard, setShowScorecard] = useState(false);
    const [birdieFlash, setBirdieFlash] = useState(false);
    const [eagleFlash, setEagleFlash] = useState(false);

    const strokeMap = useMemo(() => {
        const minHcp = Math.min(...players.map((p) => Number(p.handicap) || 0));
        return players.map((p) => {
            const playingHcp = Math.max(0, (Number(p.handicap) || 0) - minHcp);
            return allocateStrokes(playingHcp, course.holeHandicapRatings);
        });
    }, [players, course, course.holeHandicapRatings]);

    const hole = holes[current];
    const par = course.pars[current];
    const rating = course.holeHandicapRatings[current];
    const startingBet = course.startingBet ?? 3;
    const eagleEnabled = course.eagleEnabled ?? true;
    const potThisHole = holeBet(current, holes, startingBet);

    const setGross = (playerIdx, value) => {
        setHoles((prev) => {
            const next = [...prev];
            const h = { ...next[current] };
            const gross = [...(h.gross || [])];
            // grow if needed
            while (gross.length < players.length) gross.push(null);
            gross[playerIdx] =
                value === "" || value == null ? null : Math.max(1, Math.min(15, Number(value)));
            h.gross = gross;
            h.result = null; // invalidate
            next[current] = h;
            return next;
        });
    };

    const bumpGross = (playerIdx, delta) => {
        const cur = holes[current].gross[playerIdx];
        const base = cur == null ? par : cur;
        setGross(playerIdx, base + delta);
    };

    const preview = useMemo(() => {
        return computeHole({
            holeIndex: current,
            par,
            holeHandicapRatings: course.holeHandicapRatings,
            players,
            grossScores: hole.gross,
            holes,
            startingBet,
            eagleEnabled,
        });
    }, [current, par, course, course.holeHandicapRatings, players, hole, hole.gross, holes, startingBet, eagleEnabled, computeHole]);

    const commitHole = () => {
        if (preview.status === "incomplete") {
            toast.error("Enter every player's score first.");
            return;
        }
        setHoles((prev) => {
            const next = [...prev];
            next[current] = { ...next[current], result: preview };
            return next;
        });
        if (preview.eagle) {
            setEagleFlash(true);
            toast(
                `${players[preview.winnerIdx].name || `P${preview.winnerIdx + 1}`} carded an eagle — ${formatMoney(preview.payout)} pot!`,
                { icon: "🦅" },
            );
            setTimeout(() => setEagleFlash(false), 1100);
        } else if (preview.birdie) {
            setBirdieFlash(true);
            toast(
                `${players[preview.winnerIdx].name || `P${preview.winnerIdx + 1}`} carded a birdie — ${formatMoney(preview.payout)} pot!`,
                { icon: "🐦" },
            );
            setTimeout(() => setBirdieFlash(false), 900);
        } else if (preview.status === "won") {
            toast.success(
                `${players[preview.winnerIdx].name || `P${preview.winnerIdx + 1}`} takes hole ${current + 1} · ${formatMoney(preview.payout)}`,
            );
        } else if (preview.status === "tied") {
            toast(
                current < HOLE_COUNT - 1
                    ? `Tied — hole voided · hole ${current + 2} stays at $${preview.bet}`
                    : `Tied — hole voided`,
            );
        }

        // auto advance
        setTimeout(() => {
            if (current < HOLE_COUNT - 1) setCurrent((c) => c + 1);
            else onFinish();
        }, 500);
    };

    const uncommit = () => {
        setHoles((prev) => {
            const next = [...prev];
            next[current] = { ...next[current], result: null };
            return next;
        });
    };

    const standings = useMemo(
        () => computeStandings({ players, holes, startingBet }),
        [players, holes, startingBet, computeStandings],
    );
    const completedHoles = holes.filter((h) => h.result).length;

    return (
        <div className="min-h-screen flex flex-col" data-testid="play-screen">
            {/* Top bar */}
            <div
                className="sticky top-0 z-30 border-b hairline"
                style={{ background: "rgba(8,9,10,0.94)", backdropFilter: "blur(8px)" }}
            >
                <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
                    <button
                        onClick={onExit}
                        data-testid="exit-round-button"
                        className="text-[color:var(--smoke)] hover:text-[color:var(--bone)] text-xs uppercase tracking-[0.2em]"
                    >
                        ← End
                    </button>
                    <div className="flex-1 text-center">
                        <div className="eyebrow">Hole</div>
                        <div className="font-mono text-lg text-[color:var(--bone)]">
                            <span data-testid="current-hole-number">{current + 1}</span>
                            <span className="text-[color:var(--smoke)]"> / 18</span>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowScorecard((v) => !v)}
                        data-testid="toggle-scorecard-button"
                        className="text-[color:var(--smoke)] hover:text-[color:var(--bone)] text-xs uppercase tracking-[0.2em]"
                    >
                        {showScorecard ? "Play" : "Card"} →
                    </button>
                </div>
            </div>

            {!showScorecard && (
                <div className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 pt-6 pb-40">
                    {/* Hole header */}
                    <div
                        className={`surface hairline p-5 ${eagleFlash ? "eagle-flash" : ""} ${birdieFlash ? "birdie-flash" : ""}`}
                        style={{ borderRadius: 3 }}
                        data-testid="hole-card"
                    >
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="eyebrow">Hole {current + 1}</div>
                                <div className="mt-1 font-display text-4xl sm:text-5xl font-[800] leading-none">
                                    Par {par}
                                </div>
                                <div className="mt-2 text-[color:var(--smoke)] text-sm">
                                    Stroke index{" "}
                                    <span className="font-mono text-[color:var(--bone)]">
                                        {rating}
                                    </span>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="eyebrow">On the line</div>
                                <div className="mt-1 font-mono text-3xl sm:text-4xl text-[color:var(--volt)]" data-testid="hole-pot">
                                    ${potThisHole}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Player score entry */}
                    <div className="mt-4 grid gap-3">
                        {players.map((p, i) => {
                            const strokes = strokeMap[i][current];
                            const gross = hole.gross?.[i];
                            const net = gross == null ? null : gross - strokes;
                            const isEagle = eagleEnabled && gross != null && gross <= par - 2;
                            const isBirdie = !isEagle && gross === par - 1;
                            const isWinning =
                                preview.status === "won" && preview.winnerIdx === i;
                            const committed = !!hole.result;

                            return (
                                <div
                                    key={`score-row-${i}`}
                                    className="surface hairline p-3 sm:p-4"
                                    style={{
                                        borderRadius: 3,
                                        borderLeft: isWinning && isEagle
                                            ? "3px solid var(--eagle)"
                                            : isWinning
                                              ? "3px solid var(--volt)"
                                              : isEagle
                                                ? "3px solid var(--eagle)"
                                                : isBirdie
                                                  ? "3px solid var(--gold)"
                                                  : "1px solid var(--line)",
                                    }}
                                    data-testid={`score-row-${i}`}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <div>
                                            <div className="font-display uppercase tracking-wide text-sm sm:text-base font-[700]">
                                                {p.name || `Player ${i + 1}`}
                                            </div>
                                            <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--smoke)] mt-0.5">
                                                HCP{" "}
                                                <span className="font-mono text-[color:var(--bone)]">
                                                    {p.handicap}
                                                </span>
                                                {" · "}
                                                Strokes on this hole{" "}
                                                <span className="font-mono text-[color:var(--volt)]">
                                                    {strokes}
                                                </span>
                                            </div>
                                        </div>
                                        {isEagle && (
                                            <span
                                                className="chip eagle-pulse"
                                                style={{ background: "var(--eagle)", color: "#0a0b0c" }}
                                                data-testid={`eagle-chip-${i}`}
                                            >
                                                EAGLE ×4
                                            </span>
                                        )}
                                        {!isEagle && isBirdie && (
                                            <span
                                                className="chip birdie-pulse"
                                                style={{ background: "var(--gold)", color: "#0a0b0c" }}
                                                data-testid={`birdie-chip-${i}`}
                                            >
                                                BIRDIE ×2
                                            </span>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-[auto_1fr_auto_auto] gap-2 items-center">
                                        <button
                                            className="stepper-btn"
                                            data-testid={`decrement-score-${i}`}
                                            disabled={committed}
                                            onClick={() =>
                                                bumpGross(i, -1)
                                            }
                                        >
                                            −
                                        </button>
                                        <input
                                            type="number"
                                            inputMode="numeric"
                                            min={1}
                                            max={15}
                                            value={gross ?? ""}
                                            onChange={(e) => setGross(i, e.target.value)}
                                            disabled={committed}
                                            placeholder={String(par)}
                                            data-testid={`gross-input-${i}`}
                                            className="fs-input"
                                        />
                                        <button
                                            className="stepper-btn"
                                            data-testid={`increment-score-${i}`}
                                            disabled={committed}
                                            onClick={() => bumpGross(i, 1)}
                                        >
                                            +
                                        </button>
                                        <div className="min-w-[68px] text-right pl-1">
                                            <div className="eyebrow">Net</div>
                                            <div className="font-mono text-xl text-[color:var(--bone)]" data-testid={`net-score-${i}`}>
                                                {net ?? "—"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Result banner */}
                    <AnimatePresence>
                        {hole.result && (
                            <motion.div
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="mt-4 surface hairline p-4"
                                style={{
                                    borderRadius: 3,
                                    borderLeft: hole.result.eagle
                                        ? "3px solid var(--eagle)"
                                        : hole.result.birdie
                                          ? "3px solid var(--gold)"
                                          : hole.result.status === "tied"
                                            ? "3px solid var(--smoke)"
                                            : "3px solid var(--volt)",
                                }}
                                data-testid="hole-result-banner"
                            >
                                {hole.result.status === "won" ? (
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="eyebrow">
                                                {hole.result.eagle
                                                    ? "Eagle — payout quadrupled"
                                                    : hole.result.birdie
                                                      ? "Birdie — payout doubled"
                                                      : "Hole won"}
                                            </div>
                                            <div className="font-display text-2xl font-[700] mt-1">
                                                {players[hole.result.winnerIdx].name ||
                                                    `Player ${hole.result.winnerIdx + 1}`}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="eyebrow">Payout</div>
                                            <div
                                                className="font-mono text-2xl"
                                                style={{
                                                    color: hole.result.eagle
                                                        ? "var(--eagle)"
                                                        : hole.result.birdie
                                                          ? "var(--gold)"
                                                          : "var(--volt)",
                                                }}
                                            >
                                                {formatMoney(holePayout(current, holes, startingBet))}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="eyebrow">Tied — hole voided</div>
                                            <div className="font-display text-lg font-[700] mt-1">
                                                {current < HOLE_COUNT - 1
                                                    ? `Hole ${current + 2} stays at $${hole.result.bet}`
                                                    : "No winner on 18"}
                                            </div>
                                        </div>
                                        <div className="text-right eyebrow text-[color:var(--smoke)]">
                                            $0 paid
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Standings compact */}
                    <div className="mt-6">
                        <div className="eyebrow mb-2">Standings</div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {players.map((p, i) => (
                                <div
                                    key={`standing-${i}`}
                                    className="surface hairline p-3"
                                    style={{ borderRadius: 3 }}
                                    data-testid={`standing-${i}`}
                                >
                                    <div className="text-[11px] uppercase tracking-[0.15em] text-[color:var(--smoke)] truncate">
                                        {p.name || `P${i + 1}`}
                                    </div>
                                    <div
                                        className={`font-mono text-xl mt-1 ${
                                            standings[i] > 0
                                                ? "text-[color:var(--volt)]"
                                                : standings[i] < 0
                                                  ? "text-[color:var(--danger)]"
                                                  : "text-[color:var(--bone)]"
                                        }`}
                                    >
                                        {standings[i] > 0 ? "+" : ""}
                                        {formatMoney(standings[i])}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {showScorecard && (
                <div className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 pt-6 pb-40">
                    <Scorecard
                        players={players}
                        course={course}
                        holes={holes}
                        currentHole={current}
                        onSelectHole={(h) => {
                            setCurrent(h);
                            setShowScorecard(false);
                        }}
                    />
                </div>
            )}

            {/* Sticky bottom HUD */}
            <div
                className="fixed bottom-0 left-0 right-0 z-40 border-t hairline"
                style={{ background: "rgba(8,9,10,0.96)", backdropFilter: "blur(10px)" }}
            >
                <div className="max-w-5xl mx-auto px-3 sm:px-6 py-3 flex items-center gap-2">
                    <button
                        onClick={() => setCurrent((c) => Math.max(0, c - 1))}
                        disabled={current === 0}
                        data-testid="prev-hole-button"
                        className="stepper-btn"
                        aria-label="Previous hole"
                    >
                        <Chevron dir="left" />
                    </button>
                    <div className="flex-1 grid grid-cols-2 items-center gap-2">
                        <div>
                            <div className="eyebrow">Pot</div>
                            <div className="font-mono text-xl text-[color:var(--volt)]">
                                ${potThisHole}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="eyebrow">Progress</div>
                            <div className="font-mono text-xs text-[color:var(--bone)]">
                                <span data-testid="holes-complete">{completedHoles}</span>/18
                            </div>
                        </div>
                    </div>
                    {!hole.result ? (
                        <Button
                            onClick={commitHole}
                            data-testid="commit-hole-button"
                            className="btn-volt"
                            disabled={preview.status === "incomplete"}
                        >
                            {preview.status === "tied" ? "Void" : "Bank"}
                        </Button>
                    ) : (
                        <Button
                            onClick={uncommit}
                            data-testid="edit-hole-button"
                            className="btn-ghost"
                        >
                            Edit
                        </Button>
                    )}
                    <button
                        onClick={() => {
                            if (current === HOLE_COUNT - 1) onFinish();
                            else setCurrent((c) => c + 1);
                        }}
                        data-testid="next-hole-button"
                        className="stepper-btn"
                        aria-label="Next hole"
                    >
                        <Chevron dir="right" />
                    </button>
                </div>
                {completedHoles === HOLE_COUNT && (
                    <div className="max-w-5xl mx-auto px-3 sm:px-6 pb-3">
                        <Button
                            onClick={onFinish}
                            className="btn-volt w-full"
                            data-testid="finish-round-button"
                        >
                            Finish & settle up
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
